import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  MenuItem,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import { formatDateText } from "../CommonFunctions";

export default function RenewForm({
  open,
  onClose,
  tenancyDetails,
  handleRenew,
  loading = false,
}) {
  const [rentalType, setRentalType] = useState("weekly");
  const [rate, setRate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const rentalTypes = ["daily", "weekly", "monthly"];

  // Helper to compute new end date based on rental type
  const computeEndDate = (start, type) => {
    if (!start) return "";

    let startDate = new Date(start);

    switch (type) {
      case "daily":
        return format(addDays(startDate, 2), "yyyy-MM-dd");
      case "weekly":
        return format(addDays(startDate, 8), "yyyy-MM-dd");
      case "monthly":
        return format(addDays(startDate, 31), "yyyy-MM-dd");
      default:
        return start;
    }
  };

  // Load defaults on mount
  useEffect(() => {
    if (tenancyDetails && open) {
      setRate(tenancyDetails.rate || "");

      const lastCheckout = tenancyDetails.checkout;
      if (lastCheckout) {
        setFromDate(lastCheckout);
        const computedEnd = computeEndDate(lastCheckout, rentalType);
        setToDate(computedEnd);
      }
    }
  }, [tenancyDetails, open]);

  // Update end date when rentalType changes
  useEffect(() => {
    if (fromDate) {
      const newEnd = computeEndDate(fromDate, rentalType);
      setToDate(newEnd);
    }
  }, [rentalType]);

  // Update end date when fromDate is changed manually
  useEffect(() => {
    if (fromDate) {
      const newEnd = computeEndDate(fromDate, rentalType);
      setToDate(newEnd);
    }
  }, [fromDate]);

  const onRenew = () => {
    if (!fromDate || !toDate || !rate) return;
    
    handleRenew({
      rentalType,
      rate: parseFloat(rate),
      fromDate,
      toDate,
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? null : onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        Renew Tenancy
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Rental Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Rental Type</InputLabel>
              <Select
                label="Rental Type"
                value={rentalType}
                onChange={(e) => setRentalType(e.target.value)}
                disabled={loading}
              >
                {rentalTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Rate */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Rate"
              fullWidth
              size="small"
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              disabled={loading}
            />
          </Grid>

          {/* From Date (Read-only) */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="From Date"
              fullWidth
              size="small"
              type="date"
              value={fromDate}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              disabled={loading}
            />
          </Grid>

          {/* To Date (Read-only, auto-calculated) */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="To Date"
              fullWidth
              size="small"
              type="date"
              value={toDate}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              disabled={loading}
            />
          </Grid>

          {/* Info Box */}
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                bgcolor: "#e8f4ff",
                color: "#004a8f",
              }}
            >
              Tenancy will be renewed from <b>{formatDateText(fromDate)}</b> to{" "}
              <b>{formatDateText(toDate)}</b> based on the selected rental type.
            </Alert>
          </Grid>

          {/* Important Note */}
          {/* <Grid item xs={12}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <strong>Note:</strong> Guest will continue in the same room. No
              vacancy tracking or cleaning will be created.
            </Alert>
          </Grid> */}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", px: 3 }}
          color="secondary"
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={onRenew}
          disabled={!fromDate || !toDate || !rate || loading}
          sx={{ textTransform: "none", px: 4, bgcolor: "#0066ff" }}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Processing..." : "Yes, Renew"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}